/* ==================================================================
 * NERO B2B — Signup form handler (contact-form-only version)
 * Path: assets/b2b-signup.js
 * ------------------------------------------------------------------
 * Handles the B2B registration form submission:
 *   1. Validates required fields client-side (including anti-spam honeypot)
 *   2. Generates a request ID (B2B-YYYY-MM-DD-XXXXXX) for traceability
 *   3. Submits the full B2B payload to admin via Shopify /contact
 *      → Shopify sends an email to the shop's contact address set in
 *        Settings → Store details → Contact information
 *   4. Redirects to the thank-you page with the request ID
 *
 * NO customer is created at signup. Admin reviews the email, decides
 * yes/no, and — if yes — manually creates the customer in Shopify admin
 * (assigning tag `b2b-approved`, metafields, and a discount code).
 *
 * Config comes from data-* attributes on <form id="b2b-signup-form">:
 *   - data-admin-email    (fallback contact shown in error messages)
 *   - data-thank-you-url  (where to redirect on success)
 *
 * Form field names map to snippets/b2b-signup-fields.liquid
 * ================================================================== */

(function () {
  'use strict';

  var FORM_ID = 'b2b-signup-form';
  var form = document.getElementById(FORM_ID);
  if (!form) return;

  var submitBtn = form.querySelector('[data-submit-btn]');
  var submitLabel = form.querySelector('[data-submit-label]');
  var submitLoading = form.querySelector('[data-submit-loading]');
  var errorsEl = form.querySelector('.b2b-form__errors');
  var successEl = form.querySelector('.b2b-form__success');

  var cfg = {
    adminEmail: form.dataset.adminEmail || 'estore.nero@gmail.com',
    thankYouUrl: form.dataset.thankYouUrl || '/pages/b2b-thank-you'
  };

  /* ---- Helpers ---- */

  function showErrors(messages) {
    if (!errorsEl) return;
    if (!messages || !messages.length) {
      errorsEl.hidden = true;
      errorsEl.innerHTML = '';
      return;
    }
    var html = '<strong>Controlla questi campi:</strong><ul>';
    messages.forEach(function (m) { html += '<li>' + escapeHtml(m) + '</li>'; });
    html += '</ul>';
    errorsEl.innerHTML = html;
    errorsEl.hidden = false;
    errorsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function showSuccess(message) {
    if (!successEl) return;
    successEl.textContent = message;
    successEl.hidden = false;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function toggleLoading(isLoading) {
    if (!submitBtn) return;
    submitBtn.disabled = isLoading;
    submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (submitLabel) submitLabel.hidden = isLoading;
    if (submitLoading) submitLoading.hidden = !isLoading;
  }

  function generateRequestId() {
    var d = new Date();
    var pad = function (n) { return String(n).padStart(2, '0'); };
    var seed = Math.random().toString(36).substring(2, 8).toUpperCase();
    return 'B2B-' + d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + '-' + seed;
  }

  function formToPayload() {
    var fd = new FormData(form);
    var p = {};
    fd.forEach(function (value, key) { p[key] = (value || '').toString().trim(); });
    return p;
  }

  function validate(p) {
    var errors = [];

    // Honeypot — bot-filled fields bail out silently-looking
    if (p.website) {
      errors.push('Richiesta scartata (spam check).');
      return errors;
    }

    if (!p.ragione_sociale) errors.push('Ragione sociale obbligatoria.');
    if (!p.partita_iva || !/^[A-Z]{0,2}[0-9]{11}$/i.test(p.partita_iva)) {
      errors.push('Partita IVA non valida (11 cifre, prefisso paese opzionale).');
    }
    if (!p.sdi_pec) errors.push('Codice SDI o PEC obbligatori.');
    if (!p.tipologia_attivita) errors.push('Seleziona la tipologia attività.');
    if (!p.indirizzo) errors.push('Indirizzo sede obbligatorio.');
    if (!p.cap || !/^[0-9]{5}$/.test(p.cap)) errors.push('CAP non valido (5 cifre).');
    if (!p.citta) errors.push('Città obbligatoria.');
    if (!p.provincia || !/^[A-Za-z]{2}$/.test(p.provincia)) errors.push('Provincia non valida (2 lettere).');
    if (!p.primo_ordine) errors.push('Indica la taglia del primo ordine.');
    if (!p.first_name) errors.push('Nome obbligatorio.');
    if (!p.last_name) errors.push('Cognome obbligatorio.');
    if (!p.phone) errors.push('Telefono obbligatorio.');
    if (!p.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) {
      errors.push('Email non valida.');
    }
    if (!p.privacy_accepted) errors.push('Devi accettare la privacy policy per procedere.');
    return errors;
  }

  /* ---- Admin notification via Shopify /contact form ---- */

  function submitToContactForm(payload, requestId) {
    // Shopify's built-in /contact form is the simplest zero-backend way
    // to get an email to the store owner. We POST with form_type=contact
    // and Shopify routes the email to the address configured in
    // Settings → Store details → Contact information.
    var body = new URLSearchParams();
    body.append('form_type', 'contact');
    body.append('utf8', '✓');
    body.append('contact[name]', (payload.first_name || '') + ' ' + (payload.last_name || ''));
    body.append('contact[email]', payload.email || '');
    body.append('contact[phone]', payload.phone || '');
    body.append('contact[subject]', '[B2B] Nuova richiesta wholesale — ' + requestId);

    var lines = [];
    lines.push('===== RICHIESTA B2B NERÒ =====');
    lines.push('Request ID: ' + requestId);
    lines.push('Timestamp: ' + new Date().toISOString());
    lines.push('');
    lines.push('--- 01. Azienda ---');
    lines.push('Ragione sociale: ' + (payload.ragione_sociale || ''));
    lines.push('P.IVA: ' + (payload.partita_iva || ''));
    lines.push('Codice fiscale: ' + (payload.codice_fiscale || ''));
    lines.push('SDI/PEC: ' + (payload.sdi_pec || ''));
    lines.push('Tipologia: ' + (payload.tipologia_attivita || ''));
    lines.push('Indirizzo: ' + (payload.indirizzo || ''));
    lines.push('CAP/Città/Prov: ' + (payload.cap || '') + ' ' + (payload.citta || '') + ' (' + (payload.provincia || '') + ')');
    lines.push('Anno fondazione: ' + (payload.anno_fondazione || ''));
    lines.push('');
    lines.push('--- 02. Presenza online ---');
    lines.push('Sito web: ' + (payload.sito_web || ''));
    lines.push('Instagram: ' + (payload.instagram || ''));
    lines.push('');
    lines.push('--- 03. Volume previsto ---');
    lines.push('Primo ordine: ' + (payload.primo_ordine || ''));
    lines.push('Volume annuo: ' + (payload.volume_annuo || ''));
    lines.push('');
    lines.push('--- 04. Contatto ---');
    lines.push('Nome: ' + (payload.first_name || '') + ' ' + (payload.last_name || ''));
    lines.push('Ruolo: ' + (payload.ruolo || ''));
    lines.push('Telefono: ' + (payload.phone || ''));
    lines.push('Email: ' + (payload.email || ''));
    lines.push('');
    lines.push('--- 05. Note ---');
    lines.push(payload.note_richiesta || '(nessuna nota)');
    lines.push('');
    lines.push('--- Consensi ---');
    lines.push('Privacy: ' + (payload.privacy_accepted ? 'accettata' : 'non accettata'));
    lines.push('Newsletter: ' + (payload.newsletter_optin ? 'sì' : 'no'));
    lines.push('');
    lines.push('--- Prossimi passi (admin) ---');
    lines.push('1. Valuta questa richiesta');
    lines.push('2. Se approvi: Shopify admin → Customers → Add customer');
    lines.push('   - Email: ' + (payload.email || ''));
    lines.push('   - Tags: b2b-approved');
    lines.push('   - Metafields b2b.*: compila con i dati qui sopra');
    lines.push('   - Crea un discount code dedicato (es. NERO-WS-XXXX, 40% off su collection b2b-wholesale)');
    lines.push('   - Click "Send account invite"');
    lines.push('3. Se rifiuti: rispondi via email all\'indirizzo del partner');

    body.append('contact[body]', lines.join('\n'));

    return fetch('/contact#contact_form', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'text/html'
      },
      body: body.toString()
    });
  }

  /* ---- Main submit handler ---- */

  form.addEventListener('submit', function (e) {
    e.preventDefault();

    showErrors(null);
    if (successEl) successEl.hidden = true;

    var payload = formToPayload();
    payload.privacy_accepted = payload.privacy_accepted ? '1' : '';

    var errs = validate(payload);
    if (errs.length) {
      showErrors(errs);
      return;
    }

    toggleLoading(true);

    var requestId = generateRequestId();

    submitToContactForm(payload, requestId)
      .then(function () {
        // Redirect to thank-you with request id, regardless of exact
        // response code (Shopify's /contact returns 200 or 302 HTML)
        var dest = cfg.thankYouUrl + (cfg.thankYouUrl.indexOf('?') === -1 ? '?' : '&') + 'rid=' + encodeURIComponent(requestId);
        window.location.href = dest;
      })
      .catch(function (err) {
        toggleLoading(false);
        showErrors([
          'Qualcosa non ha funzionato nell\'invio: ' + (err && err.message ? err.message : 'errore sconosciuto') + '.',
          'Riprova tra un minuto. Se il problema persiste, scrivici a ' + cfg.adminEmail + ' citando il tuo nome e P.IVA.'
        ]);
      });
  });
})();
